import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, Stack, Chip, Box, Typography } from '@mui/material';
import {
  DataGrid as MuiDataGrid,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import moment from 'moment';
import ConfirmationDialog from '@components/ConfirmationDialog';

import { breadcrumbsActions } from '@store/breadcrumbs';
import { showSuccessMessage, showErrorMessage } from '@store/dashboard';
import ApplicationPageTemplate from '@dashboardComponents/ApplicationPageTemplate/ApplicationPageTemplate';

import {
  getLegacyContextualMessages,
  deleteLegacyContextualMessages,
  selectLegacyContextualMessages,
  selectIsLoadingLegacyContextualMessages,
} from '../../store/legacy-payment-options-contextual-messages';

import {
  LEGACY_CONTEXTUAL_MESSAGES_NEW_ROUTE,
  viewLegacyContextualMessages,
  LEGACY_CONTEXTUAL_MESSAGES_LIST_ROUTE,
} from '../../legacy-payment-options.breadcrumbs';

import {
  COLUMN_WIDTH,
  CONTEXTUAL_MESSAGE_COLUMNS,
} from '../../legacy-payment-options.constants';

const MESSAGES = {
  DeleteDialogTitle: 'Delete contextual messages?',
  DeleteDialogDescription:
    'Are you sure you want to delete the selected contextual messages?',
  DeleteSuccess: 'Yay! Contextual messages has been deleted successfully.',
  DeleteFailure:
    'Oops! Something went wrong while deleting the contextual messages.',
};

function PropertyKeyToolbar({ enableDelete = false, onCreateNew, onDelete }) {
  return (
    <GridToolbarContainer sx={{ p: '12px', justifyContent: 'space-between' }}>
      {enableDelete ? (
        <Button
          variant="contained"
          onClick={onDelete}
          startIcon={<DeleteIcon />}>
          Delete
        </Button>
      ) : (
        <GridToolbarQuickFilter />
      )}
      <Stack sx={{ flexDirection: 'row', gap: '12px' }}>
        <Button
          variant="contained"
          onClick={onCreateNew}
          startIcon={<AddIcon />}>
          Add new
        </Button>
      </Stack>
    </GridToolbarContainer>
  );
}

function getRuleListFormattedValue(value, field) {
  switch (field) {
    case 'startDateTime':
      return value ? dayjs(value) : dayjs(new Date());
    case 'endDateTime':
      return value ? dayjs(value) : dayjs(new Date());
    default:
      return value;
  }
}

function getCurrencyCustomColumn(code) {
  return (
    <Chip
      label={code}
      variant="outlined"
    />
  );
}

function getMultilineCustomColumn(line1, line2) {
  return (
    <Box>
      <Typography
        variant="body1"
        fontWeight="500"
        sx={{ whiteSpace: 'break-spaces' }}>
        {line1}
      </Typography>
      {line2 && <Typography variant="caption">{line2}</Typography>}
    </Box>
  );
}

function getRuleListCustomColumn(row, value, field) {
  switch (field) {
    case 'transactionCurrency': {
      return getCurrencyCustomColumn(value);
    }
    case 'keepAlways': {
      return value.toString().toUpperCase();
    }
    case 'startDateTime': {
      const { startDateTime } = row;
      const date = moment(startDateTime).format('MMMM Do YYYY');
      const time = moment(startDateTime).format('h:mm:ss a');

      return getMultilineCustomColumn(date, time);
    }
    case 'endDateTime': {
      const { endDateTime } = row;
      const date = moment(endDateTime).format('MMMM Do YYYY');
      const time = moment(endDateTime).format('h:mm:ss a');

      return getMultilineCustomColumn(date, time);
    }
    default:
      return null;
  }
}

function getRuleListColumns() {
  return CONTEXTUAL_MESSAGE_COLUMNS.map(
    ({ width = COLUMN_WIDTH.MD, ...col }) => ({
      ...col,
      width,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: ({ value }) =>
        getRuleListFormattedValue(value, col.field),
      renderCell: ({ value, row }) =>
        getRuleListCustomColumn(row, value, col.field),
    }),
  );
}

export default function ContextualMessagesList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState({ show: false });

  const enableDelete = selectedRows.length !== 0;

  const contextualMessages = useSelector(selectLegacyContextualMessages);
  const isLoadingContextualMessages = useSelector(
    selectIsLoadingLegacyContextualMessages,
  );

  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(viewLegacyContextualMessages));
    dispatch(getLegacyContextualMessages(false));
  }, [dispatch]);

  const onCreateNew = () => {
    navigate(LEGACY_CONTEXTUAL_MESSAGES_NEW_ROUTE);
  };

  const onDelete = () => {
    setShowDeleteModal({ show: true });
  };

  const onDeleteConfirmation = (mappingList) => {
    dispatch(deleteLegacyContextualMessages(mappingList)).then(
      ({ payload }) => {
        if (payload && payload.status === 'SUCCESS') {
          dispatch(showSuccessMessage(MESSAGES.DeleteSuccess));
          dispatch(getLegacyContextualMessages(false));
        } else {
          dispatch(
            showErrorMessage({
              message: MESSAGES.DeleteFailure,
              duration: 4000,
            }),
          );
        }
      },
    );
  };

  return (
    <ApplicationPageTemplate
      title="Contextual Messages"
      pageReady>
      <MuiDataGrid
        getRowHeight={() => 'auto'}
        density="comfortable"
        loading={isLoadingContextualMessages}
        rows={contextualMessages}
        checkboxSelection
        disableSelectionOnClick
        disableColumnFilter
        disableColumnMenu
        disableColumnSorting
        disableColumnSelector
        disableDensitySelector
        onSelectionModelChange={(row) => {
          const selection = row.map((id) => id);
          setSelectedRows(selection);
        }}
        components={{
          Toolbar: () =>
            PropertyKeyToolbar({
              enableDelete,
              onCreateNew,
              onDelete,
            }),
        }}
        componentsProps={{
          toolbar: {
            csvOptions: { disableToolbarButton: true },
            printOptions: { disableToolbarButton: true },
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        onRowClick={({ row }) => {
          navigate(`${LEGACY_CONTEXTUAL_MESSAGES_LIST_ROUTE}/${row.id}`);
        }}
        getRowId={({ id }) => `${id}`}
        columns={getRuleListColumns()}
      />

      <ConfirmationDialog
        openDialog={showDeleteModal}
        yesHandler={onDeleteConfirmation}
        data={selectedRows}
        title={MESSAGES.DeleteDialogTitle}
        message={MESSAGES.DeleteDialogDescription}
      />
    </ApplicationPageTemplate>
  );
}
